import * as React from 'react';
import { Link } from 'wouter';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, Mail, MessageSquare, Phone } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth, type SiteRole } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';

interface SubscriptionDraft {
  siteId: string;
  notifyViaSms: boolean;
  notifyViaEmail: boolean;
}

export function PreferencesPage() {
  const qc = useQueryClient();
  const { me, refetch } = useAuth();

  const [phone, setPhone] = React.useState<string>('');
  const [phoneError, setPhoneError] = React.useState<string | null>(null);
  const [subs, setSubs] = React.useState<Record<string, SubscriptionDraft>>({});
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (!me) return;
    setPhone(me.user.phoneE164 ?? '');
    const map: Record<string, SubscriptionDraft> = {};
    for (const a of me.siteAccess) {
      map[a.siteId] = {
        siteId: a.siteId,
        notifyViaSms: a.notifyViaSms,
        notifyViaEmail: a.notifyViaEmail,
      };
    }
    setSubs(map);
    setDirty(false);
  }, [me]);

  const save = useMutation({
    mutationFn: (body: {
      phoneE164: string | null | undefined;
      siteSubscriptions: SubscriptionDraft[];
    }) => api.patch('/api/me/preferences', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      refetch();
      toast.success('Preferences saved');
      setDirty(false);
    },
    onError: (err: ApiError) => toast.error(err.message),
  });

  const onSave = () => {
    setPhoneError(null);
    let phoneE164: string | null | undefined;
    if (phone.trim() === '') {
      phoneE164 = null;
    } else if (!/^\+\d{10,15}$/.test(phone.trim())) {
      setPhoneError('E.164 format only — e.g. +16025551234');
      return;
    } else {
      phoneE164 = phone.trim();
    }
    save.mutate({
      phoneE164,
      siteSubscriptions: Object.values(subs),
    });
  };

  const hasPhone = phone.trim() !== '' && /^\+\d{10,15}$/.test(phone.trim());

  return (
    <div className="px-6 md:px-10 py-8 max-w-3xl">
      <div className="mb-2">
        <Link
          href="/"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Home
        </Link>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-black tracking-tight">My Preferences</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Phone number and per-site notification channels. Site assignments are managed by an admin.
        </p>
      </div>

      <div className="rounded-lg border border-card-border bg-card p-5 card-shadow mb-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Phone className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold">Phone number</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Required for SMS alerts. E.164 format with country code, e.g. <span className="font-mono">+16025551234</span>. Clear to disable SMS everywhere.
            </p>
          </div>
        </div>
        <div className="mt-4 space-y-1.5">
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            inputMode="tel"
            placeholder="+16025551234"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setDirty(true);
            }}
            className="font-mono"
          />
          {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
        </div>
      </div>

      <div className="rounded-lg border border-card-border bg-card p-5 card-shadow mb-4">
        <h2 className="text-base font-semibold">Notification channels</h2>
        <p className="text-sm text-muted-foreground mt-0.5 mb-4">
          For each site you can receive alerts on. Critical alerts (missed tour, hot box temp
          breach) and warnings (partial tour, stale temp) follow these toggles.
        </p>

        {!me || me.siteAccess.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-4 text-sm text-muted-foreground">
            No site assignments yet. An admin needs to add you to a site first.
          </div>
        ) : (
          <ul className="space-y-2">
            {me.siteAccess.map((a) => {
              const draft = subs[a.siteId];
              if (!draft) return null;
              return (
                <li
                  key={a.siteId}
                  className="rounded-md border border-card-border bg-card px-3 py-2.5"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{a.siteName}</div>
                      <div className="text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-[10px]">
                          {roleLabel(a.role)}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="inline-flex items-center gap-1.5 text-xs">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        Email
                        <Switch
                          checked={draft.notifyViaEmail}
                          onCheckedChange={(v) => {
                            setSubs((prev) => ({
                              ...prev,
                              [a.siteId]: { ...draft, notifyViaEmail: v },
                            }));
                            setDirty(true);
                          }}
                        />
                      </label>
                      <label className="inline-flex items-center gap-1.5 text-xs">
                        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                        SMS
                        <Switch
                          checked={hasPhone && draft.notifyViaSms}
                          disabled={!hasPhone}
                          onCheckedChange={(v) => {
                            setSubs((prev) => ({
                              ...prev,
                              [a.siteId]: { ...draft, notifyViaSms: v },
                            }));
                            setDirty(true);
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {!hasPhone && me && me.siteAccess.length > 0 && (
          <p className="text-[11px] text-muted-foreground mt-3">
            Add a phone number above to enable SMS toggles.
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button
          onClick={onSave}
          disabled={save.isPending || !dirty}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

function roleLabel(role: SiteRole): string {
  return `Site.${role[0]!.toUpperCase()}${role.slice(1)}`;
}
